<template>
  <div class="login-card">
    <div class="card-header">
      <h2 class="card-title">{{ displayTitle }}</h2>
      <p class="card-subtitle">
        <span class="brand-name">{{ brandName }}</span>
        <span class="brand-dot"> · </span>
        <span class="brand-tag">{{ t('loginPage.tagline') }}</span>
      </p>
    </div>

    <el-form
      ref="formRef"
      class="login-form"
      :rules="rules"
      label-position="top"
      :model="formLabelAlign"
      size="large"
      @submit.prevent
    >
      <el-form-item :label="t('loginPage.mobile')" prop="name">
        <el-input
          v-model="formLabelAlign.name"
          :prefix-icon="User"
          :placeholder="t('loginPage.mobilePh')"
          autocomplete="tel"
        />
      </el-form-item>
      <el-form-item :label="t('loginPage.verificationCode')" prop="emil" class="code-item">
        <el-input
          v-model="formLabelAlign.emil"
          :prefix-icon="Message"
          :placeholder="t('loginPage.codePh')"
          autocomplete="one-time-code"
        />
        <button type="button" class="code-btn">{{ t('loginPage.sendCode') }}</button>
      </el-form-item>
      <el-form-item :label="t('loginPage.accountPassword')" prop="passwrod">
        <el-input
          v-model="formLabelAlign.passwrod"
          :prefix-icon="Lock"
          type="password"
          :placeholder="t('loginPage.passwordPh')"
          autocomplete="new-password"
          show-password
        />
      </el-form-item>
      <el-form-item :label="t('loginPage.confirmPassword')" prop="confirmPsd">
        <el-input
          v-model="formLabelAlign.confirmPsd"
          :prefix-icon="Lock"
          type="password"
          :placeholder="t('loginPage.confirmPasswordPh')"
          autocomplete="new-password"
          show-password
        />
      </el-form-item>

      <el-button
        class="submit-btn"
        type="primary"
        native-type="button"
        @click="register(formRef)"
      >
        {{ btnType === 0 ? t('loginPage.register') : t('loginPage.changePasswordBtn') }}
      </el-button>
    </el-form>

    <div class="card-footer">
      <button type="button" class="link-btn" @click="emit('login')">
        {{ t('loginPage.backToLogin') }}
        <span class="link-arrow" aria-hidden="true">→</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, defineEmits, defineProps } from 'vue'
import { User, Lock, Message } from '@element-plus/icons-vue'
import { DEFAULT_BRAND_NAME } from '@/utils/tenantBranding.js'
import { usePageI18n } from '@/composables/usePageI18n.js'

const brandName = DEFAULT_BRAND_NAME
const emit = defineEmits(['login'])
const props = defineProps({
  title: { type: String, default: '' },
  btnType: { type: Number, default: 0 },
})
const formRef = ref()

const rules = reactive({
  emil: [{ required: true, message: '' }],
  name: [{ required: true, message: '' }],
  confirmPsd: [{ required: true, message: '' }],
  passwrod: [{ required: true, message: '' }],
})

const applyLabels = () => {
  rules.emil[0].message = t('loginPage.codeRequired')
  rules.name[0].message = t('loginPage.usernameRequired')
  rules.confirmPsd[0].message = t('loginPage.confirmPasswordRequired')
  rules.passwrod[0].message = t('loginPage.passwordRequired')
}
const { t } = usePageI18n(applyLabels)
applyLabels()

const displayTitle = computed(() => {
  if (props.title) return props.title
  return props.btnType === 0 ? t('loginPage.register') : t('loginPage.forgotPwd')
})

const formLabelAlign = reactive({
  emil: '',
  name: '',
  passwrod: '',
  confirmPsd: '',
})

const register = (formEl) => {
  if (!formEl) return
  formEl.validate(() => {})
}
</script>

<style lang="less" scoped>
.login-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 40px 40px 32px;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 12px 40px rgba(15, 23, 42, 0.08);
}

.card-header {
  margin-bottom: 28px;
}

.card-title {
  margin: 0 0 8px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0f172a;
  line-height: 1.2;
}

.card-subtitle {
  margin: 0;
  line-height: 1.5;
  color: #64748b;
}

.card-subtitle .brand-name {
  font-size: 19px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.02em;
}

.card-subtitle .brand-dot {
  font-size: 16px;
  color: #94a3b8;
}

.card-subtitle .brand-tag {
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
  letter-spacing: 0.04em;
}

.login-form {
  :deep(.el-form-item) {
    margin-bottom: 20px;
  }

  :deep(.el-form-item__label) {
    padding-bottom: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #334155;
  }

  :deep(.el-input__wrapper) {
    border-radius: 10px;
    box-shadow: 0 0 0 1px #e2e8f0 inset;
    padding: 4px 12px;

    &.is-focus {
      box-shadow: 0 0 0 2px #2563eb inset;
    }
  }

  :deep(.el-input__inner) {
    height: 42px;
    font-size: 15px;
  }
}

.code-item {
  position: relative;

  .code-btn {
    position: absolute;
    right: 12px;
    top: 38px;
    padding: 0;
    border: none;
    background: none;
    font-size: 13px;
    font-weight: 500;
    color: #2563eb;
    cursor: pointer;
  }
}

.submit-btn {
  width: 100%;
  height: 44px;
  margin-top: 4px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
}

.card-footer {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f1f5f9;
  text-align: center;
}

.link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #2563eb;
  cursor: pointer;
}

.link-arrow {
  transition: transform 0.15s ease;
}

.link-btn:hover .link-arrow {
  transform: translateX(3px);
}
</style>
